/**
 * Transforms JSON:API page-possibilities OR single book-page responses
 * into nested layer trees.
 *
 * Supports two response shapes:
 *  - Possibilities: { data: Array<PagePossibility>, included: [...] }  → transformPageLayers / transformAllPages
 *  - Single page:   { data: PageObject,             included: [...] }  → transformSinglePage
 *
 * Key observations from comparing input → output:
 *
 * 1. SCOPE: Process ONE page possibility at a time (selected by index).
 *    The page's relationships.layers.data gives the ordered list of layer refs.
 *
 * 2. RESOLUTION: Each layer ref { id, type } is looked up in `included`.
 *    The full attributes + flattened relationships become the output object.
 *
 * 3. TYPE NORMALIZATION:
 *    Books::PagePossibilityImageLayer → Books::PageImageLayer
 *    Books::PagePossibilityTextLayer  → Books::PageTextLayer
 *    Books::PagePossibilityLayer      → Books::PageTextLayer  (container/block)
 *
 * 4. RELATIONSHIP FLATTENING:
 *    - Image layers  → photo, thumbnail:{}, source-media, canvas-image:{}, event-destination-layer
 *    - Other layers  → source-media, event-destination-layer
 *    - null data     → {}  (never null in output, always empty object)
 *
 * 5. event-destination-layer on rounded_corners child:
 *    Points to the sibling MasterExpression (photo) layer within the same parent.
 *    This is sourced directly from the layer's own relationships in `included`.
 *
 * 6. TREE BUILDING via media-block-id:
 *    - Layers with a media-block-id are children of the layer with that id
 *    - Only root layers (no media-block-id or media-block-id not in set) are top-level
 *    - Both root and children are sorted by render-order ascending
 *
 * 7. ui_element.margins layer (render-order: 0) is EXCLUDED from the output.
 *    It is a pure UI decoration layer with no content.
 */

/**
 * Normalize the JSON:API "Possibility" type strings into standard layer types.
 * Books::PagePossibilityLayer (container blocks) → Books::PageTextLayer
 * because they carry text-like attributes (font-*, text) in the data model.
 *
 * Already-normalized types from book-page-layers (single page API) pass through
 * unchanged — so this function is safe to call on both response types.
 *
 * @param {string} rawType
 * @returns {string}
 */
function normalizeLayerType(rawType) {
    const typeMap = {
        // Possibility variants → normalized
        'Books::PagePossibilityImageLayer': 'Books::PageImageLayer',
        'Books::PagePossibilityTextLayer': 'Books::PageTextLayer',
        'Books::PagePossibilityLayer': 'Books::PageTextLayer',
        // Already-normalized — pass through (book-page-layers from single page API)
        'Books::PageImageLayer': 'Books::PageImageLayer',
        'Books::PageTextLayer': 'Books::PageTextLayer',
        'Books::PageLayer': 'Books::PageTextLayer',
    };
    return typeMap[rawType] ?? rawType;
}

/**
 * Flatten a JSON:API relationships object into plain key→value pairs.
 *
 * Rules:
 *  - Image layers get:  photo, thumbnail, source-media, canvas-image, event-destination-layer
 *  - Other layers get:  source-media, event-destination-layer
 *  - Any relationship whose data is null → {} (empty object, never null)
 *  - thumbnail and canvas-image are always {} (they are never populated in this API)
 *
 * @param {Object} relationships  - The raw `relationships` block from included
 * @param {string} normalizedType - Already-normalized type string
 * @returns {Object}
 */
function flattenRelationships(relationships = {}, normalizedType) {
    const isImageLayer = normalizedType === 'Books::PageImageLayer';

    // Helper: extract { id, type } from a relationship data node, or {} if null
    const ref = (relKey) => {
        const data = relationships[relKey]?.data;
        return data ? { id: data.id, type: data.type } : {};
    };

    if (isImageLayer) {
        return {
            'photo': ref('photo'),
            'thumbnail': {},  // Always empty in this API
            'source-media': ref('source-media'),
            'canvas-image': {},  // Always empty in this API
            'event-destination-layer': ref('event-destination-layer'),
        };
    }

    // Text layers, container/block layers
    return {
        'source-media': ref('source-media'),
        'event-destination-layer': ref('event-destination-layer'),
    };
}

/**
 * Build an O(1) lookup map from the `included` array, keyed by item id.
 * Used by both transformPageLayers and transformSinglePage.
 *
 * @param {Array<Object>} included
 * @returns {Map<string, Object>}
 */
function buildIncludedMap(included = []) {
    const map = new Map();
    included.forEach((item) => map.set(item.id, item));
    return map;
}

/**
 * Resolve a single layer reference { id, type } into a fully flat layer object
 * by looking it up in the includedById map, normalizing its type, and
 * flattening its relationships.
 *
 * Shared by both transformPageLayers and transformSinglePage.
 *
 * @param {{ id: string, type: string }} layerRef
 * @param {Map<string, Object>}          includedById
 * @returns {Object|null}  null if the layer was not found in included
 */
function resolveLayerRef({ id, type }, includedById) {
    const included = includedById.get(id);

    if (!included) {
        console.warn(
            `[resolveLayerRef] Layer "${id}" (${type}) not found in included. Skipping.`
        );
        return null;
    }

    const { attributes, relationships } = included;
    const normalizedType = normalizeLayerType(attributes.type);
    const flatRels = flattenRelationships(relationships, normalizedType);

    return {
        // All scalar attributes spread first
        ...attributes,

        // Override type with the normalized version
        // (no-op for already-normalized book-page-layers types)
        type: normalizedType,

        // Flattened relationship fields merged in
        // (after attributes so they overwrite any accidental collision)
        ...flatRels,

        // children array — populated in buildLayerTree
        children: [],
    };
}

/**
 * Filter out pure UI decoration layers that never appear in the output.
 * Currently excludes: component-variable === 'ui_element' (e.g. margins layers)
 *
 * @param {Array<Object>} layers
 * @returns {Array<Object>}
 */
function filterDecorationLayers(layers) {
    return layers.filter(
        (layer) => layer['component-variable'] !== 'ui_element'
    );
}

/**
 * Build the parent-child layer tree from a flat resolved array, using
 * media-block-id to wire children into their parent's .children array.
 *
 * Algorithm (mirrors optimizeLayers in the React components):
 *   a. Build a map of id → layer for O(1) parent lookup
 *   b. For each layer with a media-block-id that exists in THIS layer set,
 *      push it into the parent's .children array and mark it as a child
 *   c. Return only root layers (not marked as children), sorted by render-order
 *   d. Sort children within each parent by render-order as well
 *
 * Shared by both transformPageLayers and transformSinglePage.
 *
 * @param {Array<Object>} contentLayers - Flat, decoration-filtered, resolved layers
 * @returns {Array<Object>}             - Root layers with nested children
 */
function buildLayerTree(contentLayers) {
    /** @type {Map<string, Object>} id → resolved layer object */
    const layerById = new Map();
    contentLayers.forEach((layer) => layerById.set(layer.id, layer));

    /** @type {Set<string>} ids of layers that are children of another layer */
    const childIds = new Set();

    contentLayers.forEach((layer) => {
        const parentId = layer['media-block-id'];

        // Only wire up if the parent actually exists in THIS page's layer set.
        // Guards against dangling media-block-id references across pages.
        if (parentId && layerById.has(parentId)) {
            layerById.get(parentId).children.push(layer);
            childIds.add(layer.id);
        }
    });

    // Root layers = those not claimed as a child, sorted by render-order
    const rootLayers = contentLayers
        .filter((layer) => !childIds.has(layer.id))
        .sort((a, b) => a['render-order'] - b['render-order']);

    // Sort children within each parent as well
    rootLayers.forEach((layer) => {
        if (layer.children.length > 1) {
            layer.children.sort((a, b) => a['render-order'] - b['render-order']);
        }
    });

    return rootLayers;
}

/**
 * Core pipeline: given layer refs and an includedById map, resolve → filter → tree.
 * Extracted so both transform functions share identical processing logic.
 *
 * @param {Array<{ id: string, type: string }>} layerRefs
 * @param {Map<string, Object>}                  includedById
 * @returns {Array<Object>}
 */
function processLayers(layerRefs, includedById) {
    const resolvedLayers = layerRefs
        .map((ref) => resolveLayerRef(ref, includedById))
        .filter(Boolean); // drop nulls from missing included entries

    const contentLayers = filterDecorationLayers(resolvedLayers);

    return buildLayerTree(contentLayers);
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API — Possibilities response  (data is an Array)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Transform a page-possibilities API response into a nested layer tree
 * for ONE selected page possibility.
 *
 * Input:  { data: Array<PagePossibility>, included: Array<Layer|Image> }
 * Output: Array<LayerObject>  (root layers with nested children)
 *
 * @param {Object} apiResponse  - Full possibilities response
 * @param {number} pageIndex    - Which page possibility to process (0-based)
 * @returns {Array<Object>}
 */
function transformPageLayers(apiResponse, pageIndex = 0) {
    // ── 1. Index the `included` array ──────────────────────────────────────────
    const includedById = buildIncludedMap(apiResponse.included);

    // ── 2. Select the target page ───────────────────────────────────────────────
    const targetPage = apiResponse.data[pageIndex];
    if (!targetPage) {
        throw new Error(
            `transformPageLayers: no page at index ${pageIndex}. ` +
            `Available indices: 0–${apiResponse.data.length - 1}`
        );
    }

    const layerRefs = targetPage.relationships?.layers?.data ?? [];

    // ── 3 → 6. Resolve, filter, build tree ────────────────────────────────────
    return processLayers(layerRefs, includedById);
}

/**
 * Transform ALL page possibilities in one call.
 *
 * Input:  { data: Array<PagePossibility>, included: Array<Layer|Image> }
 * Output: Array<{ id, pageName, attributes, layers }>
 *
 * @param {Object} apiResponse
 * @returns {Array<{ id: string, pageName: string, attributes: Object, kind: string, layers: Array }>}
 */
export function transformAllPages(apiResponse) {
    return apiResponse?.data.map((page, index) => ({
        id: page.id,
        pageName: page.attributes.name,
        kind: page.attributes.kind,
        attributes: page.attributes,
        layers: transformPageLayers(apiResponse, index),
    }));
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API — Single book-page response  (data is an Object)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Transform a single book-page API response into a nested layer tree.
 *
 * Key differences from transformPageLayers:
 *  - apiResponse.data is a single Object, not an Array
 *  - Layer refs use type "page-layers"; included items use type "book-page-layers"
 *    (lookup is by id so the type mismatch in refs is harmless)
 *  - Attribute types are already normalized (Books::PageImageLayer etc.)
 *    so normalizeLayerType is a no-op — but still called for consistency
 *  - Returns { page: PageAttributes, layers: Array } instead of a bare Array
 *
 * Input:  { data: PageObject, included: Array<Layer|Image> }
 * Output: { page: Object, layers: Array<LayerObject> }
 *
 * @param {Object} apiResponse - Single page API response
 * @returns {{ page: Object, layers: Array<Object> }}
 */
export function transformSinglePage(apiResponse) {
    // ── 1. Index the `included` array ──────────────────────────────────────────
    const includedById = buildIncludedMap(apiResponse.included);

    // ── 2. Extract layer refs from the single page object ──────────────────────
    const pageData = apiResponse.data;
    const layerRefs = pageData.relationships?.layers?.data ?? [];

    // ── 3 → 6. Resolve, filter, build tree  (same pipeline as possibilities) ──
    const layers = processLayers(layerRefs, includedById);

    // Return page metadata alongside the layer tree
    return {
        id: pageData.id,
        pageName: pageData.attributes.name,
        kind: pageData.attributes.kind,
        attributes: pageData.attributes,
        layers,
    };
}

export function getCategory(kind) {
    const milestones = ['milestones_detail', 'milestones_title'];
    const watchMeGrow = ['wmg_new_born', 'wmg_one_pager', 'growth_chart'];
    const tft = ['tft_baby_parents', 'tft_everyone_else_three_column'];

    if (tft.includes(kind)) {
        return 'family-tree';
    }

    if (milestones.includes(kind)) {
        return 'milestones';
    }

    if (watchMeGrow.includes(kind)) {
        return 'watch-me-grow';
    }

    return null; // or throw an error if appropriate
}

// Bound the ReactAvatarEditor canvas size. Two problems to solve:
//   1. A single canvas can't exceed the browser's area limit (iOS/mobile Safari reports
//      67,108,864 px²) or it silently fails to render.
//   2. Watch Me Grow renders MANY editors at once (one per month). ReactAvatarEditor
//      multiplies width/height by devicePixelRatio for its backing store, so at the
//      browser's per-canvas limit each one is ~240MB — a dozen of them exhaust mobile
//      Safari's memory and crash the app.
// The editor is only ever shown at <=~300px, so a small backing store is plenty. We cap
// to a mobile-safe area well under the browser limit; scale/position are relative, so the
// visible crop is unchanged. (Bump this only if edit-preview sharpness is ever an issue.)
const MAX_CANVAS_AREA = 4194304; // 2048² — ~16MB backing per canvas at dpr 1
export const capCanvasDims = (width, height) => {
    if (!width || !height) return { width, height };
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    const safeArea = MAX_CANVAS_AREA * 0.9;
    const backingArea = width * height * dpr * dpr;
    if (backingArea <= safeArea) return { width, height };
    const factor = Math.sqrt(safeArea / backingArea);
    return { width: Math.floor(width * factor), height: Math.floor(height * factor) };
};
